import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { NotificationHubService } from 'src/core/services/signalr/notification-hub.service';
import { NotificationService } from './notification.service';
import { NotificationCountSummaryDto } from 'src/core/models/notification/notification-count-summary.dto';
import { NotificationListRequestDto } from 'src/core/models/notification/notification-list-request.dto';
import { SystemNotificationReceivedPayload } from 'src/core/models/notification/system-notification-received.payload';
import {
  resolveNotificationToastMessage,
  shouldShowToastForType,
} from 'src/app/shared/components/notification-bell/resolve-notification-item-component.helper';

// Matches AgentStatusToolbarComponent's own AGENT_DESKTOP_PATH — the call-handling screen suppresses
// generic toasts (this one included) so they never interrupt/overlap the live-call UI.
const AGENT_DESKTOP_PATH = '/agent-desktop';

/**
 * Signal-based state holder for the notification bell (no NGXS in this codebase — see
 * `AgentStatusService` for the established root-service pattern this mirrors: state as signals, discrete
 * events as RxJS Subjects). Owns the hub connection end-to-end: subscribes to `systemNotificationReceived$`
 * once and reacts to every push (refresh the Recent/Unread/Total counts, show a toast) so behavior is
 * centralized regardless of how many `NotificationBellComponent` instances get created/destroyed by the
 * NavItem host. The actual notification list is owned per-page (see `NotificationMainComponent`) since its
 * data (filtered, paginated) differs from this service's own concerns.
 */
@Injectable({ providedIn: 'root' })
export class NotificationStateService {
  private readonly notificationService = inject(NotificationService);
  private readonly hubService = inject(NotificationHubService);
  private readonly toasterService = inject(ToasterService);
  private readonly router = inject(Router);

  /** Recent/Unread/Total counts — plain state, read directly wherever needed (see `AgentStatusService`
   *  for the established root-service signal convention this mirrors). */
  readonly countSummary = signal<NotificationCountSummaryDto>(new NotificationCountSummaryDto());

  private readonly notificationsChangedSource = new Subject<void>();

  /**
   * Fires once per hub push or reconnect, after the count summary has been refreshed — the single signal
   * any list-owning component (see `NotificationMainComponent`) should subscribe to instead of duplicating
   * a subscription to `NotificationHubService`'s own streams directly. Kept as an RxJS `Subject` rather
   * than a signal: it's a discrete "something happened" pulse with no value to hold as state (same
   * reasoning as `AgentStatusService.statusChanged$`), and a signal could silently collapse two pulses
   * that land in the same tick, which is fine for animation triggers but not guaranteed safe here.
   */
  readonly notificationsChanged$: Observable<void> = this.notificationsChangedSource.asObservable();

  private initialized = false;

  /** Idempotent — safe to call from every `NotificationBellComponent.ngOnInit`. */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.hubService.systemNotificationReceived$.subscribe(payload => this.handlePushReceived(payload));
    this.hubService.hubReconnected$.subscribe(() => this.handleHubReconnected());

    try {
      await this.hubService.connect();
      await this.refreshCountSummary();
    } catch (error) {
      console.error('[NotificationStateService] Failed to connect notification hub', error);
    }
  }

  /**
   * Sourced from the REST list endpoint (one minimal-page request per status) rather than the hub's
   * `Request`/CountSummary invoke — that path turned out unreliable (unconfirmed even in the reference app
   * it was modeled after). The REST list is already proven to work, so `totalCount` from a status-filtered,
   * single-row page is a simpler and more trustworthy source for the badge and the Recent/Unread/Total
   * breadcrumb counts on the full notifications page.
   */
  async refreshCountSummary(): Promise<void> {
    try {
      const [unread, recent, total] = await Promise.all([
        firstValueFrom(this.notificationService.getList(this.buildCountRequest('unread'))),
        firstValueFrom(this.notificationService.getList(this.buildCountRequest('recent'))),
        firstValueFrom(this.notificationService.getList(this.buildCountRequest('all'))),
      ]);

      const summary = new NotificationCountSummaryDto();
      summary.unread = unread.totalCount;
      summary.recent = recent.totalCount;
      summary.total = total.totalCount;
      this.countSummary.set(summary);
    } catch (error) {
      console.error('[NotificationStateService] Failed to refresh count summary', error);
    }
  }

  private buildCountRequest(status: string): NotificationListRequestDto {
    const request = new NotificationListRequestDto();
    request.status = status;
    request.maxResultCount = 1;
    return request;
  }

  private handlePushReceived(payload: SystemNotificationReceivedPayload): void {
    void this.refreshCountSummary();
    this.notificationsChangedSource.next();

    const type = payload.type ?? payload.Type ?? '';
    if (this.router.url.startsWith(AGENT_DESKTOP_PATH) || !shouldShowToastForType(type)) {
      return;
    }

    const rawPayload = payload.payload ?? payload.Payload ?? '';
    const toast = resolveNotificationToastMessage(type, rawPayload);
    this.toasterService.info(
      toast.messageParams?.length ? { key: toast.messageKey, params: toast.messageParams } : toast.messageKey,
      toast.titleKey,
      { iconClass: toast.iconClass },
    );
  }

  /** Catches up on anything that may have been pushed while the connection was dropped. */
  private handleHubReconnected(): void {
    void this.refreshCountSummary();
    this.notificationsChangedSource.next();
  }
}
